import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Play, FileSearch, AlertTriangle, CheckCircle2, CheckCircle } from "lucide-react";

export default function AdminNormalizarEntregada() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applied, setApplied] = useState(null);

  const dryRun = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/normalizar-entregada/dry-run");
      setReport(data);
      toast.message(`Informe: ${data.lineas_actualizar} línea(s) y ${data.peticiones_recalcular} petición(es) a recalcular`);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  const apply = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/admin/normalizar-entregada/apply");
      setApplied(data);
      toast.success(`Aplicado: ${data.lineas_actualizadas} líneas, ${data.peticiones_recalculadas} peticiones recalculadas`);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5" data-testid="page-admin-normalizar-entregada">
      <div className="flex items-start gap-3">
        <div className="bg-amber-500/15 border border-amber-500/30 rounded-lg p-2.5">
          <CheckCircle className="w-6 h-6 text-amber-300" />
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-zinc-100">Cerrar peticiones con montajes completados</h1>
          <p className="text-sm text-zinc-400 mt-0.5 leading-snug max-w-3xl">
            Marca como <code className="text-amber-300">completada</code> las líneas que tienen TODAS sus paradas cerradas como <code>montado</code>/<code>semi_montado</code>,
            y recalcula el estado de la petición (<code>entregada</code> si todas las líneas están, o <code>montado_parcial</code> si solo algunas).
            Útil para regularizar datos antiguos en los que el hook no se aplicó.
          </p>
        </div>
      </div>

      <Card className="bg-zinc-900/40 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-base text-zinc-200">1. Vista previa (no toca BD)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={dryRun} disabled={loading} data-testid="btn-dryrun"
            className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSearch className="w-4 h-4 mr-2" />}
            Analizar (dry-run)
          </Button>

          {report && (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-3 text-zinc-300">
                <Badge className="bg-amber-500/20 text-amber-300 border-amber-500/30">{report.lineas_actualizar} líneas</Badge>
                <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30">{report.peticiones_recalcular} peticiones</Badge>
              </div>
              {report.candidatos?.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-zinc-500 border-b border-zinc-800">
                        <th className="py-1.5 pr-3">Petición</th>
                        <th className="py-1.5 pr-3">Cliente</th>
                        <th className="py-1.5 pr-3">Línea</th>
                        <th className="py-1.5 pr-3">Estado actual</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.candidatos.slice(0, 100).map((c, i) => (
                        <tr key={i} className="border-b border-zinc-900">
                          <td className="py-1 pr-3 font-mono text-amber-300">{c.peticion_codigo}</td>
                          <td className="py-1 pr-3 text-zinc-300">{c.cliente || "—"}</td>
                          <td className="py-1 pr-3 text-zinc-400">{c.linea_descripcion}</td>
                          <td className="py-1 pr-3"><Badge className="text-[10px] bg-zinc-700/40 text-zinc-300">{c.peticion_estado}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/40 border-amber-500/30">
        <CardHeader>
          <CardTitle className="text-base text-amber-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> 2. Aplicar cambios
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-zinc-400">
            Marca las líneas como completadas y recalcula el estado de las peticiones afectadas (forzando la transición si hace falta).
          </p>
          <Button onClick={apply} disabled={loading} data-testid="btn-apply"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-bold">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Aplicar normalización
          </Button>

          {applied && (
            <div className="space-y-2 text-sm bg-emerald-500/10 border border-emerald-500/30 rounded p-3">
              <div className="flex items-center gap-2 text-emerald-300">
                <CheckCircle2 className="w-4 h-4" /> Aplicado: {applied.lineas_actualizadas} líneas, {applied.peticiones_recalculadas} peticiones recalculadas
              </div>
              {applied.estados_resultantes && (
                <div className="text-xs text-zinc-300">
                  Estados resultantes:{" "}
                  {Object.entries(applied.estados_resultantes).map(([k, v]) => (
                    <Badge key={k} className="mr-1 bg-zinc-800 border-zinc-700 text-zinc-300 text-[10px]">{k}: {v}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
